import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import styles from './styles.module.scss';

const KeyFeatures = ({
  items = [],
  title = null,
  theme = null,
  description = null,
  itemTextAlign = 'center',
  itemsPerRow = 3,
}) => {
  const textAlignClass = useMemo(
    () => (itemTextAlign === 'left' ? styles.textAlignLeft : styles.textAlignCenter),
    [itemTextAlign]
  );

  const itemsPerRowClass = useMemo(() => {
    switch (itemsPerRow) {
      case 2:
        return styles.itemsPerRow2;
      case 3:
        return styles.itemsPerRow3;
      default:
        return undefined;
    }
  }, [itemsPerRow]);

  if (!items || items.length === 0) {
    return null;
  }

  // Render appropriate child component based on device size
  return (
    <section
      className={cn(styles.keyFeatures, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      <div className={styles.inner}>
        <div className="container">
          {title && (
            <Heading className={styles.title} tag="h2" size="xl">
              {title}
            </Heading>
          )}
          {description && (
            <div className={styles.text} dangerouslySetInnerHTML={{ __html: description }} />
          )}
          <div className={cn(styles.items, textAlignClass, itemsPerRowClass)}>
            {items.map((item, index) => (
              <div
                key={`key-item-${index}`}
                className={cn(styles.itemCell, { [styles.itemLast]: index === items.length - 1 })}
              >
                <div className={styles.item}>
                  <div>
                    <div className={styles.itemTitle}>{item.title}</div>
                    <div className={styles.itemDescription}>{item.description}</div>
                  </div>
                  {item.buttonLabel && item.buttonLink && (
                    <div className={styles.itemButton}>
                      <Button
                        to={item.buttonLink}
                        className={styles.btn}
                        size="sm"
                        theme="accent-primary"
                        ghost={theme !== 'dark'}
                      >
                        {item.buttonLabel}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

KeyFeatures.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      buttonLabel: PropTypes.string,
      buttonLink: PropTypes.string,
    })
  ),
  itemTextAlign: PropTypes.oneOf(['left', 'center']),
  itemsPerRow: PropTypes.oneOf([1, 2, 3]),
};

export default KeyFeatures;
